/*
 * From the network's numbers to boxes. Pure, and the twin of backend/ai/device_boxes.py's `decode`: both run
 * detect_cases.json. The ONNX file stops at the head's cells so this stays in one place, on both sides.
 *
 * A cell holds [x, y, log w, log h, objectness, ...classes]; the last two are probabilities already. A picture sits at
 * the top left of the square, the rest padded, so a box comes back as a fraction of the picture alone.
 *
 * It runs on every frame in the worker, inside the 200 ms budget, so there's no array per cell: a cell whose
 * objectness is under the score can't pass it, whatever its classes say, and most cells end there.
 */
export function letterboxRatio(width, height, size) {
  return Math.min(size / width, size / height);
}

function iou(a, b) {
  const ix = Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1));
  const iy = Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1));
  const inter = ix * iy;
  const union = (a.x2 - a.x1) * (a.y2 - a.y1) + (b.x2 - b.x1) * (b.y2 - b.y1) - inter;
  return union > 0 ? inter / union : 0;
}

const unit = v => Math.min(1, Math.max(0, v));

/** `raw` is flat, cells x (5 + classes). [{x1, y1, x2, y2, score, cls}] in fractions of the picture, best first. */
export function decodeCells(raw, { width, height, size, strides, classes, score, iou: overlap, limit = 20 }) {
  const stride = 5 + classes;
  const ratio = letterboxRatio(width, height, size);
  const found = [];
  let at = 0;
  for (const s of strides) {
    const side = Math.floor(size / s);
    for (let y = 0; y < side; y += 1) {
      for (let x = 0; x < side; x += 1, at += stride) {
        const objectness = raw[at + 4];
        if (objectness >= score) {
          let cls = 0;
          let best = raw[at + 5];
          for (let c = 1; c < classes; c += 1) {
            if (raw[at + 5 + c] > best) {
              best = raw[at + 5 + c];
              cls = c;
            }
          }
          const value = objectness * best;
          if (value >= score) {
            const cx = (raw[at] + x) * s;
            const cy = (raw[at + 1] + y) * s;
            const halfW = (Math.exp(raw[at + 2]) * s) / 2;
            const halfH = (Math.exp(raw[at + 3]) * s) / 2;
            found.push({
              x1: unit((cx - halfW) / ratio / width),
              y1: unit((cy - halfH) / ratio / height),
              x2: unit((cx + halfW) / ratio / width),
              y2: unit((cy + halfH) / ratio / height),
              score: value,
              cls,
            });
          }
        }
      }
    }
  }
  found.sort((a, b) => b.score - a.score);
  const kept = [];
  for (const box of found) {
    if (kept.every(other => other.cls !== box.cls || iou(box, other) <= overlap)) kept.push(box);
    if (kept.length === limit) break;
  }
  return kept;
}

/** RGBA as the network reads it: the manifest's channel order at 0-255, one plane after another. */
export function pixelsToTensor(rgba, size, order = 'bgr') {
  const n = size * size;
  const out = new Float32Array(3 * n);
  const [first, third] = order === 'bgr' ? [2, 0] : [0, 2];
  for (let i = 0; i < n; i += 1) {
    out[i] = rgba[i * 4 + first];
    out[n + i] = rgba[i * 4 + 1];
    out[2 * n + i] = rgba[i * 4 + third];
  }
  return out;
}
